import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  onSnapshot, 
  where, 
  orderBy, 
  updateDoc, 
  doc, 
  addDoc, 
  Timestamp,
  increment,
  limit
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { 
  Wallet, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  DollarSign, 
  ArrowRight,
  User,
  Calendar,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownLeft,
  Building2,
  FileText,
  Eye,
  Plus,
  ArrowRightLeft,
  MapPin,
  ExternalLink,
  ChevronRight,
  History,
  Info,
  Printer
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { CashRequisition, Account, Expense, OfficeLocation, Settings } from '../types';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const CashierDashboard: React.FC = () => {
  const { profile, isCashier, isAccountant, isAdmin } = useAuth();
  const [requisitions, setRequisitions] = useState<CashRequisition[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [locations, setLocations] = useState<OfficeLocation[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'pending_release' | 'released' | 'pipeline' | 'rejected'>('pending_release');

  const [selectedReq, setSelectedReq] = useState<CashRequisition | null>(null);
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [releaseAmount, setReleaseAmount] = useState<number>(0);

  // Manual Expense State
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: 0,
    category: 'General',
    accountId: ''
  });

  // Transfer State
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferData, setTransferData] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: 0,
    description: 'Internal Transfer'
  });

  useEffect(() => {
    // Requisitions approved by MD but not yet released
    const reqQuery = query(
      collection(db, 'cash_requisitions'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeReq = onSnapshot(reqQuery, (snapshot) => {
      setRequisitions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashRequisition)));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'cash_requisitions');
    });

    // Accounts for cash release
    const accountsQuery = query(collection(db, 'accounts'), where('type', '==', 'cash'));
    const unsubscribeAccounts = onSnapshot(accountsQuery, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
    });

    // Recent expenses
    const expensesQuery = query(collection(db, 'expenses'), orderBy('date', 'desc'), limit(10));
    const unsubscribeExpenses = onSnapshot(expensesQuery, (snapshot) => {
      setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Expense)));
    });

    // Office Locations for direction links
    const unsubscribeLocations = onSnapshot(collection(db, 'office_locations'), (snapshot) => {
      setLocations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as OfficeLocation)));
    });

    const unsubscribeSettings = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setSettings({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Settings);
      }
    });

    return () => {
      unsubscribeReq();
      unsubscribeAccounts();
      unsubscribeExpenses();
      unsubscribeLocations();
      unsubscribeSettings();
    };
  }, []);

  const generatePDF = (req: CashRequisition) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header - Letterhead
    if (settings?.letterheadUrl) {
      doc.addImage(settings.letterheadUrl, 'PNG', 0, 0, 210, 40);
    } else {
      doc.setFontSize(20);
      doc.setTextColor(30, 58, 138); // blue-900
      doc.text('BRIGHT TECHNICAL SYSTEMS', pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.text('GENERAL SUPPLIES LIMITED', pageWidth / 2, 26, { align: 'center' });
    }

    const startY = settings?.letterheadUrl ? 45 : 35;

    // Title
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('CASH REQUISITION FORM', pageWidth / 2, startY, { align: 'center' });
    
    // Requisition Info
    doc.setFontSize(10);
    doc.text(`Code No: ${req.codeNo}`, 14, startY + 10);
    doc.text(`Date: ${req.requisitionDate?.toDate?.()?.toLocaleDateString() || 'N/A'}`, pageWidth - 14, startY + 10, { align: 'right' });
    
    doc.text(`Site Name: ${req.siteName}`, 14, startY + 18);
    doc.text(`Branch: ${req.branchName || 'N/A'}`, 14, startY + 24);
    doc.text(`Requested By: ${req.requestedBy}`, 14, startY + 30);
    doc.text(`Status: ${req.status.toUpperCase().replace('_', ' ')}`, pageWidth - 14, startY + 30, { align: 'right' });

    // Items Table
    const tableData = req.items.map((item, index) => [
      index + 1,
      item.purpose,
      item.quantity,
      new Intl.NumberFormat('en-US').format(item.rate),
      new Intl.NumberFormat('en-US').format(item.amount)
    ]);

    autoTable(doc, {
      startY: startY + 35,
      head: [['S/N', 'Purpose', 'Qty', 'Rate (TSh)', 'Amount (TSh)']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [30, 58, 138], textColor: 255, fontStyle: 'bold' },
      foot: [['', '', '', 'TOTAL', new Intl.NumberFormat('en-US').format(req.totalAmount)]],
      footStyles: { fillColor: [241, 245, 249], textColor: 0, fontStyle: 'bold' },
    });

    const finalY = (doc as any).lastAutoTable.finalY + 15;

    // Approval History
    doc.setFontSize(12);
    doc.text('Approval History', 14, finalY);
    
    const approvalData = req.approvals?.map(app => [
      app.step,
      app.approverName,
      app.status.toUpperCase(),
      app.date?.toDate?.()?.toLocaleString() || '-',
      app.comment || '-'
    ]) || [];

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Step', 'Approver', 'Status', 'Date', 'Comment']],
      body: approvalData,
      theme: 'plain',
      headStyles: { fontStyle: 'bold' },
      styles: { fontSize: 8 }
    });

    // Footer
    const footerY = doc.internal.pageSize.height - 20;
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('This is a computer generated document.', pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Printed on: ${new Date().toLocaleString()}`, pageWidth / 2, footerY + 5, { align: 'center' });

    downloadFile(doc.output('blob'), `Requisition_${req.codeNo}.pdf`);
  };

  const handleReleaseCash = async () => {
    if (!selectedReq || !selectedAccountId || releaseAmount <= 0) {
      toast.error('Please select an account and enter a valid amount');
      return;
    }
    
    const account = accounts.find(a => a.id === selectedAccountId);
    if (!account) return;

    if (account.balance < releaseAmount) {
      toast.error(`Insufficient funds in ${account.name}. Available: ${formatCurrency(account.balance)}`);
      return;
    }

    try {
      const isPartial = releaseAmount < (selectedReq.totalAmount - (selectedReq.releasedAmount || 0));
      const newReleasedAmount = (selectedReq.releasedAmount || 0) + releaseAmount;
      const isFullyReleased = newReleasedAmount >= selectedReq.totalAmount;

      // 1. Update requisition status
      await updateDoc(doc(db, 'cash_requisitions', selectedReq.id), {
        status: isFullyReleased ? 'released' : 'approved', // Keep as approved if partial
        releasedAmount: newReleasedAmount,
        releasedAt: Timestamp.now(),
        releasedBy: profile?.displayName,
        releasedById: profile?.uid,
        accountId: selectedAccountId,
        accountName: account.name
      });

      // 2. Update account balance
      await updateDoc(doc(db, 'accounts', selectedAccountId), {
        balance: increment(-releaseAmount),
        updatedAt: Timestamp.now()
      });

      // 3. Register expense
      await addDoc(collection(db, 'expenses'), {
        amount: releaseAmount,
        category: 'Cash Requisition',
        description: `${isPartial ? 'Partial' : 'Full'} cash release for requisition ${selectedReq.codeNo} - ${selectedReq.siteName}`,
        date: Timestamp.now(),
        accountId: selectedAccountId,
        accountName: account.name,
        requisitionId: selectedReq.id,
        status: 'paid',
        createdAt: Timestamp.now()
      });

      toast.success(isFullyReleased ? 'Cash released successfully' : `Partial amount of ${formatCurrency(releaseAmount)} released`);
      setIsReleaseModalOpen(false);
      setSelectedReq(null);
      setSelectedAccountId('');
      setReleaseAmount(0);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `cash_requisitions/${selectedReq.id}`);
      toast.error('Failed to release cash');
    }
  };

  const handleManualExpense = async () => {
    if (!newExpense.description || !newExpense.amount || !newExpense.accountId) {
      toast.error('Please fill all fields');
      return;
    }

    const account = accounts.find(a => a.id === newExpense.accountId);
    if (!account || account.balance < newExpense.amount) {
      toast.error('Insufficient funds');
      return;
    }

    try {
      await addDoc(collection(db, 'expenses'), {
        ...newExpense,
        date: Timestamp.now(),
        accountName: account.name,
        status: 'paid',
        createdAt: Timestamp.now()
      });

      await updateDoc(doc(db, 'accounts', newExpense.accountId), {
        balance: increment(-newExpense.amount),
        updatedAt: Timestamp.now()
      });

      toast.success('Expense recorded');
      setIsExpenseModalOpen(false);
      setNewExpense({ description: '', amount: 0, category: 'General', accountId: '' });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'expenses');
      toast.error('Failed to record expense');
    }
  };

  const handleTransfer = async () => {
    if (!transferData.fromAccountId || !transferData.toAccountId || !transferData.amount) {
      toast.error('Please fill all fields');
      return;
    }

    if (transferData.fromAccountId === transferData.toAccountId) {
      toast.error('Cannot transfer to the same account');
      return;
    }

    const fromAcc = accounts.find(a => a.id === transferData.fromAccountId);
    if (!fromAcc || fromAcc.balance < transferData.amount) {
      toast.error('Insufficient funds in source account');
      return;
    }

    try {
      // Deduct from source
      await updateDoc(doc(db, 'accounts', transferData.fromAccountId), {
        balance: increment(-transferData.amount),
        updatedAt: Timestamp.now()
      });

      // Add to destination
      await updateDoc(doc(db, 'accounts', transferData.toAccountId), {
        balance: increment(transferData.amount),
        updatedAt: Timestamp.now()
      });

      // Record as two expenses or a special transfer log
      await addDoc(collection(db, 'expenses'), {
        description: `Transfer to ${accounts.find(a => a.id === transferData.toAccountId)?.name}: ${transferData.description}`,
        amount: transferData.amount,
        category: 'Transfer Out',
        date: Timestamp.now(),
        accountId: transferData.fromAccountId,
        accountName: fromAcc.name,
        status: 'paid',
        createdAt: Timestamp.now()
      });

      toast.success('Transfer successful');
      setIsTransferModalOpen(false);
      setTransferData({ fromAccountId: '', toAccountId: '', amount: 0, description: 'Internal Transfer' });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'accounts');
      toast.error('Transfer failed');
    }
  };

  const filteredRequisitions = requisitions.filter(req => {
    const matchesSearch = 
      req.codeNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.siteName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.requestedBy.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = false;
    if (statusFilter === 'pending_release') {
      matchesStatus = req.status === 'approved';
    } else if (statusFilter === 'released') {
      matchesStatus = req.status === 'released';
    } else if (statusFilter === 'pipeline') {
      matchesStatus = ['pending_hod', 'pending_accountant', 'pending_ops_manager', 'pending_md'].includes(req.status);
    } else if (statusFilter === 'rejected') {
      matchesStatus = req.status === 'rejected';
    }
    
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount).replace('TZS', 'TSh');
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading cashier dashboard...</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Cashier Dashboard</h1>
          <p className="text-slate-500 font-medium tracking-tight">Manage cash releases and track daily expenses.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GenerateReportTrigger />
          <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200">
            <button
              onClick={() => setStatusFilter('pipeline')}
              className={cn(
                "px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2",
                statusFilter === 'pipeline' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
              )}
            >
              Pipeline
              <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[10px]">
                {requisitions.filter(r => ['pending_hod', 'pending_accountant', 'pending_ops_manager', 'pending_md'].includes(r.status)).length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('pending_release')}
              className={cn(
                "px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2",
                statusFilter === 'pending_release' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
              )}
            >
              Pending Release
              <span className="bg-emerald-100 text-emerald-600 px-2 py-0.5 rounded-full text-[10px]">
                {requisitions.filter(r => r.status === 'approved').length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('released')}
              className={cn(
                "px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2",
                statusFilter === 'released' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
              )}
            >
              Released
              <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-[10px]">
                {requisitions.filter(r => r.status === 'released').length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={cn(
                "px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2",
                statusFilter === 'rejected' ? "bg-white text-red-600 shadow-sm" : "text-slate-500 hover:text-red-600"
              )}
            >
              Rejected
              <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-[10px]">
                {requisitions.filter(r => r.status === 'rejected').length}
              </span>
            </button>
        </div>
      </div>
    </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm">
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search requisitions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
              />
            </div>

            <div className="space-y-4">
              {filteredRequisitions.map((req) => (
                <motion.div
                  key={req.id}
                  layout
                  className="bg-slate-50 p-6 rounded-3xl border border-transparent hover:border-blue-900/10 hover:bg-white hover:shadow-xl transition-all"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-start gap-5 flex-1">
                      <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-7 h-7" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-black text-blue-900">{req.codeNo}</h3>
                          <span className={cn(
                            "px-2 py-0.5 text-white text-[8px] font-black uppercase tracking-widest rounded-md",
                            req.status === 'approved' ? "bg-emerald-500" : 
                            req.status === 'released' ? "bg-blue-500" : "bg-orange-500"
                          )}>
                            {req.status.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-900 font-bold mb-1">{req.siteName}</p>
                        <p className="text-xs text-slate-500 font-medium mb-2">Requested by: {req.requestedBy}</p>
                        <div className="flex flex-wrap items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {req.requisitionDate?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </div>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {req.branchName}
                          </div>
                          {req.jobCardNo && (
                            <div className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              JC: {req.jobCardNo}
                            </div>
                          )}
                        </div>
                        {req.status === 'rejected' && req.approvals?.some(a => a.status === 'rejected') && (
                          <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <p className="text-[8px] font-black text-red-400 uppercase tracking-widest mb-1">Rejection Reason</p>
                            <p className="text-xs text-red-700 font-bold italic">
                              "{req.approvals.find(a => a.status === 'rejected')?.comment || 'No reason provided'}"
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <p className="text-xl font-black text-slate-900">{formatCurrency(req.totalAmount)}</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedReq(req);
                            setIsViewModalOpen(true);
                          }}
                          className="p-2.5 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-all"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {(req.status === 'released' || req.status === 'rejected') && (
                          <button
                            onClick={() => generatePDF(req)}
                            className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all"
                            title="Print Requisition"
                          >
                            <Printer className="w-4 h-4" />
                          </button>
                        )}
                        {req.status === 'approved' && (
                          <button
                            onClick={() => {
                              setSelectedReq(req);
                              setReleaseAmount(req.totalAmount - (req.releasedAmount || 0));
                              setIsReleaseModalOpen(true);
                            }}
                            className="px-6 py-2.5 bg-blue-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                          >
                            Release Cash
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              {filteredRequisitions.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Wallet className="w-10 h-10 text-slate-200" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No requisitions found</h3>
                  <p className="text-slate-500">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => setIsExpenseModalOpen(true)}
              className="flex flex-col items-center justify-center gap-2 p-6 bg-white rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Plus className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Record Expense</span>
            </button>
            <button
              onClick={() => setIsTransferModalOpen(true)}
              className="flex flex-col items-center justify-center gap-2 p-6 bg-white rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <ArrowRightLeft className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">Transfer Funds</span>
            </button>
            <button
              onClick={() => {
                // Trigger the floating button's modal if possible, or just link to it.
                // Since the modal is in App.tsx, we can't easily trigger it from here without a global state.
                // But we can add a simple instruction or just keep the two main cashier forms.
                // Actually, let's add a "View Reports" link instead.
                window.location.hash = '#/reports';
              }}
              className="flex flex-col items-center justify-center gap-2 p-6 bg-white rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">View Reports</span>
            </button>
          </div>

          <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm">
            <h2 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-blue-900" />
              Cash Accounts
            </h2>
            <div className="space-y-4">
              {accounts.map(account => (
                <div key={account.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{account.name}</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[8px] font-black uppercase rounded">Cash</span>
                  </div>
                  <p className="text-xl font-black text-slate-900">{formatCurrency(account.balance)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm">
            <h2 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-2">
              <ArrowUpRight className="w-6 h-6 text-orange-500" />
              Recent Expenses
            </h2>
            <div className="space-y-4">
              {expenses.map(expense => (
                <div key={expense.id} className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-2xl transition-colors">
                  <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900 truncate">{expense.description}</p>
                    <p className="text-[10px] text-slate-400 font-medium">{expense.date?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                  </div>
                  <p className="text-sm font-black text-red-600">-{formatCurrency(expense.amount)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Release Modal */}
      <AnimatePresence>
        {isReleaseModalOpen && selectedReq && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsReleaseModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Release Cash</h2>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{selectedReq.codeNo}</p>
                  </div>
                </div>
                <button onClick={() => setIsReleaseModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-center">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Amount</p>
                    <p className="text-xl font-black text-slate-900">{formatCurrency(selectedReq.totalAmount)}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 text-center">
                    <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Remaining</p>
                    <p className="text-xl font-black text-blue-900">{formatCurrency(selectedReq.totalAmount - (selectedReq.releasedAmount || 0))}</p>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Amount to Release</label>
                  <div className="relative">
                    <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                    <input
                      type="number"
                      value={releaseAmount || ''}
                      onChange={(e) => setReleaseAmount(parseFloat(e.target.value))}
                      placeholder="Enter amount..."
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-bold text-lg"
                    />
                  </div>
                  {releaseAmount > (selectedReq.totalAmount - (selectedReq.releasedAmount || 0)) && (
                    <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">Amount exceeds remaining balance</p>
                  )}
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Select Cash Account</label>
                  <div className="space-y-2">
                    {accounts.map(account => (
                      <button
                        key={account.id}
                        onClick={() => setSelectedAccountId(account.id)}
                        className={cn(
                          "w-full p-4 rounded-2xl border-2 transition-all text-left flex items-center justify-between",
                          selectedAccountId === account.id 
                            ? "border-blue-900 bg-blue-50/50 shadow-md" 
                            : "border-slate-100 bg-white hover:border-slate-200"
                        )}
                      >
                        <div>
                          <p className="text-sm font-bold text-slate-900">{account.name}</p>
                          <p className="text-[10px] text-slate-400 font-medium">Balance: {formatCurrency(account.balance)}</p>
                        </div>
                        {selectedAccountId === account.id && <CheckCircle2 className="w-5 h-5 text-blue-900" />}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleReleaseCash}
                  disabled={!selectedAccountId}
                  className="w-full py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Cash Release
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {isViewModalOpen && selectedReq && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsViewModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Requisition Details</h2>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{selectedReq.codeNo}</p>
                  </div>
                </div>
                <button onClick={() => setIsViewModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Site & Branch</p>
                      <p className="font-bold text-slate-900">{selectedReq.siteName}</p>
                      <p className="text-sm text-slate-500">{selectedReq.branchName}</p>
                      {locations.find(l => l.name === selectedReq.siteName) && (
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${locations.find(l => l.name === selectedReq.siteName)?.latitude},${locations.find(l => l.name === selectedReq.siteName)?.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-blue-600 text-[10px] font-bold uppercase tracking-widest hover:underline"
                        >
                          <MapPin className="w-3 h-3" />
                          View Directions
                        </a>
                      )}
                    </div>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Requested By</p>
                      <p className="font-bold text-slate-900">{selectedReq.requestedBy}</p>
                      <p className="text-sm text-slate-500">Date: {selectedReq.requisitionDate?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Status</p>
                      <p className="font-bold text-slate-900 uppercase tracking-wider">{selectedReq.status.replace('_', ' ')}</p>
                      <p className="text-sm text-slate-500">Step: {selectedReq.currentStep}/4</p>
                    </div>
                    <div className="bg-blue-900 p-4 rounded-2xl text-white">
                      <p className="text-[10px] font-black opacity-60 uppercase tracking-widest mb-1">Total Amount</p>
                      <p className="text-2xl font-black">{formatCurrency(selectedReq.totalAmount)}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Approval History
                  </h3>
                  <div className="space-y-3">
                    {selectedReq.approvals?.map((approval, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                          approval.status === 'approved' ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"
                        )}>
                          {approval.status === 'approved' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900">{approval.step}</p>
                          <p className="text-xs text-slate-500">By {approval.approverName} on {approval.date?.toDate?.()?.toLocaleString() || 'N/A'}</p>
                          {approval.comment && (
                            <p className="mt-1 text-xs text-slate-600 italic">"{approval.comment}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {(!selectedReq.approvals || selectedReq.approvals.length === 0) && (
                      <p className="text-sm text-slate-400 italic text-center py-4">No approval history yet</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Items ({selectedReq.items?.length || 0})</h3>
                  <div className="space-y-2">
                    {selectedReq.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl">
                        <div>
                          <p className="text-sm font-bold text-slate-900">{item.purpose}</p>
                          <p className="text-xs text-slate-500">{item.quantity} x {formatCurrency(item.rate)}</p>
                        </div>
                        <p className="font-black text-slate-900">{formatCurrency(item.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Manual Expense Modal */}
      <AnimatePresence>
        {isExpenseModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsExpenseModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
                    <Plus className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">Record Expense</h2>
                </div>
                <button onClick={() => setIsExpenseModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Description</label>
                  <input
                    type="text"
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    placeholder="e.g. Office Supplies"
                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Amount</label>
                    <input
                      type="number"
                      value={newExpense.amount || ''}
                      onChange={(e) => setNewExpense({ ...newExpense, amount: parseFloat(e.target.value) })}
                      placeholder="0.00"
                      className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Category</label>
                    <select
                      value={newExpense.category}
                      onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                      className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                    >
                      <option value="General">General</option>
                      <option value="Supplies">Supplies</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Utilities">Utilities</option>
                      <option value="Transport">Transport</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Account</label>
                  <select
                    value={newExpense.accountId}
                    onChange={(e) => setNewExpense({ ...newExpense, accountId: e.target.value })}
                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                  >
                    <option value="">Select Account</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleManualExpense}
                  className="w-full py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                >
                  Save Expense
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Transfer Modal */}
      <AnimatePresence>
        {isTransferModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTransferModalOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                    <ArrowRightLeft className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-black text-slate-900 tracking-tight">Transfer Funds</h2>
                </div>
                <button onClick={() => setIsTransferModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">From Account</label>
                  <select
                    value={transferData.fromAccountId}
                    onChange={(e) => setTransferData({ ...transferData, fromAccountId: e.target.value })}
                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                  >
                    <option value="">Select Source</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">To Account</label>
                  <select
                    value={transferData.toAccountId}
                    onChange={(e) => setTransferData({ ...transferData, toAccountId: e.target.value })}
                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                  >
                    <option value="">Select Destination</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name} ({formatCurrency(acc.balance)})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1">Amount</label>
                  <input
                    type="number"
                    value={transferData.amount || ''}
                    onChange={(e) => setTransferData({ ...transferData, amount: parseFloat(e.target.value) })}
                    placeholder="0.00"
                    className="w-full px-5 py-4 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-blue-900 transition-all font-medium"
                  />
                </div>

                <button
                  onClick={handleTransfer}
                  className="w-full py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
                >
                  Confirm Transfer
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(' ');
}

const X = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default CashierDashboard;
